import { AmendmentEntry, AmendmentValue } from './AmendmentHistory';

/**
 * SKIN-374 — turns a patient's personal-data history, as the API serves it
 * (`GET .../patient/:uuidPatient/personal-data/history`, one row per field,
 * oldest first, ciphertext + nonce), into AmendmentHistory entries: the rows
 * one registration or one edit wrote become one entry, newest first, with the
 * values decrypted here in the browser (the API never can).
 */

export interface PersonalDataHistoryRow {
    id: number;
    action: 'CREATED' | 'UPDATED';
    fieldName: string;
    oldValue: string | null;
    oldNonce: string | null;
    newValue: string | null;
    newNonce: string | null;
    reason: string | null;
    createdAt: string;
    actor: { uuid: string; name: string | null; surnames: string | null };
}

/** How one personal field reads in the history. */
export interface PersonalFieldView {
    label: string;
    encrypted: boolean;
    /** Plaintext as shown, e.g. a birthdate's epoch-ms as a date. */
    format: (plain: string) => string;
}

/** Decrypts one stored value (nonce present: AES-GCM; absent: legacy CryptoJS), or null when it cannot. */
export type DecryptPersonalValue = (ciphertext: string, nonce: string | null) => Promise<string | null>;

const ACTION: Record<PersonalDataHistoryRow['action'], AmendmentEntry['action']> = {
    CREATED: 'CREATED',
    UPDATED: 'EDIT',
};

/** Rows of one registration or one edit share their transaction's timestamp, actor and reason. */
function sameWrite(a: PersonalDataHistoryRow, b: PersonalDataHistoryRow): boolean {
    return a.action === b.action && a.createdAt === b.createdAt && a.actor.uuid === b.actor.uuid && a.reason === b.reason;
}

async function toValue(raw: string | null, nonce: string | null, field: PersonalFieldView | undefined, decrypt: DecryptPersonalValue): Promise<AmendmentValue> {
    if (raw === null || raw === '') return { kind: 'empty' };
    const format = field?.format ?? ((plain: string) => plain);
    if (field && !field.encrypted) return { kind: 'text', text: format(raw) };
    const plain = await decrypt(raw, nonce);
    if (plain === null) return { kind: 'encrypted' };
    return plain === '' ? { kind: 'empty' } : { kind: 'text', text: format(plain) };
}

export async function toPersonalDataEntries(
    rows: PersonalDataHistoryRow[],
    fields: Record<string, PersonalFieldView>,
    decrypt: DecryptPersonalValue,
): Promise<AmendmentEntry[]> {
    const writes: PersonalDataHistoryRow[][] = [];
    for (const row of rows) {
        const current = writes[writes.length - 1];
        if (current && sameWrite(current[0], row)) current.push(row);
        else writes.push([row]);
    }
    const entries = await Promise.all(writes.map(async (write): Promise<AmendmentEntry> => {
        const [first] = write;
        return {
            id: first.id,
            action: ACTION[first.action],
            actorName: [first.actor.name, first.actor.surnames].filter(Boolean).join(' ') || first.actor.uuid,
            at: first.createdAt,
            reason: first.reason,
            changes: await Promise.all(write.map(async (row) => {
                const field = fields[row.fieldName];
                return {
                    kind: 'value' as const,
                    label: field?.label ?? row.fieldName,
                    oldValue: await toValue(row.oldValue, row.oldNonce, field, decrypt),
                    newValue: await toValue(row.newValue, row.newNonce, field, decrypt),
                };
            })),
        };
    }));
    return entries.reverse();
}
